import styled from "@emotion/styled";
import { map, slice } from "lodash-es";
import { useEffect, useMemo, useState } from "react";

import { VirtualizedItemList } from "@/components/item-list";
import { ItemListRowType } from "@/components/item-list/types";
import type { ItemListRowData } from "@/components/item-list/types";
import { MDSText, MDSTextColor, MDSTextVariant } from "@/mds";
import {
  AgentWorkEmptyState,
  AgentWorkEmptyStateKind,
} from "@/pages/agent/components/agent-work-empty-state";
import { AgentTaskListItem } from "@/pages/agent/tasks/agent-task-list-item";
import type { TaskRowViewModel } from "@/pages/agent/tasks/agent-tasks-page.utils";

export const AgentTasksList = ({
  taskRows,
  dataLoaded,
  queryKey,
  showCreationGuidance = false,
  showProject = true,
  onOpenNote,
  onOpenProject,
  onOpenTask,
  onOpenActivities,
  onToggleTaskComplete,
}: {
  taskRows: TaskRowViewModel[];
  /** False until the assistant stores have synced, to tell empty from loading. */
  dataLoaded: boolean;
  queryKey: string;
  showCreationGuidance?: boolean;
  showProject?: boolean;
  onOpenNote: ({ noteId }: { noteId: string }) => void;
  onOpenProject: ({ projectId }: { projectId: string }) => void;
  onOpenTask: ({ taskId }: { taskId: string }) => void;
  onOpenActivities: ({ taskId }: { taskId: string }) => void;
  onToggleTaskComplete: ({ taskId }: { taskId: string }) => void;
}) => {
  const [visibleTaskCount, setVisibleTaskCount] = useState(AGENT_TASK_PAGE_SIZE);

  useEffect(() => {
    setVisibleTaskCount(AGENT_TASK_PAGE_SIZE);
  }, [queryKey]);

  const virtualizedTaskRows: ItemListRowData[] = useMemo(() => {
    const visibleTaskRows = slice(taskRows, 0, visibleTaskCount);

    return map(visibleTaskRows, taskRow => ({
      key: taskRow.task.id,
      type: ItemListRowType.Other,
      payload: {
        children: (
          <AgentTaskListItem
            showProject={showProject}
            taskRow={taskRow}
            onOpenActivities={onOpenActivities}
            onOpenNote={onOpenNote}
            onOpenProject={onOpenProject}
            onOpenTask={onOpenTask}
            onToggleTaskComplete={onToggleTaskComplete}
          />
        ),
      },
    }));
  }, [
    onOpenActivities,
    onOpenNote,
    onOpenProject,
    onOpenTask,
    onToggleTaskComplete,
    showProject,
    taskRows,
    visibleTaskCount,
  ]);

  if (taskRows.length === 0 && dataLoaded && showCreationGuidance) {
    return <AgentWorkEmptyState kind={AgentWorkEmptyStateKind.Tasks} />;
  }

  return (
    <StyledTaskList>
      {taskRows.length === 0 && dataLoaded ? (
        <StyledEmptyState>
          <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.TextS}>
            No tasks in this view
          </MDSText>
        </StyledEmptyState>
      ) : (
        <StyledTaskListViewport aria-label="Tasks" role="list">
          <StyledTaskListFill role="presentation">
            <VirtualizedItemList
              key={queryKey}
              endReached={() => {
                setVisibleTaskCount(currentCount =>
                  Math.min(currentCount + AGENT_TASK_PAGE_SIZE, taskRows.length)
                );
              }}
              loading={!dataLoaded && taskRows.length === 0}
              registerWithNavigation={false}
              rows={virtualizedTaskRows}
            />
          </StyledTaskListFill>
        </StyledTaskListViewport>
      )}
    </StyledTaskList>
  );
};

const AGENT_TASK_PAGE_SIZE = 50;

const StyledTaskList = styled.div`
  background: ${({ theme }) => theme.colors.background.input.chrome.default};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
`;

const StyledTaskListViewport = styled.div`
  flex: 1 1 auto;
  min-height: 240px;
  min-width: 0;
  position: relative;
`;

const StyledTaskListFill = styled.div`
  inset: 0;
  position: absolute;
`;

const StyledEmptyState = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 240px;
  padding: ${({ theme }) => theme.spacing[6]};
`;
